import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { NgrxValueConverter } from 'ngrx-forms';
import { ZefFormBase } from '@zerops/zef/forms';
import {
  HttpRoutingFormLocationsConfigValue,
  HttpRoutingFormLocationsValue,
  HttpRoutingFormState
} from '@zerops/zui/http-routing-form';
import { HttpPortsWithServiceStackInfo } from '@zerops/models/http-routing';
import { FormControlState, Boxed, FormArrayState } from 'ngrx-forms';
import { HttpRoutingFieldsTranslations } from './http-routing-fields.translations';

export enum HttpRoutingFieldsModes {
  Normal = 'normal',
  Fallback = 'fallback'
}

@Component({
  selector: 'zui-http-routing-fields',
  templateUrl: './http-routing-fields.component.html',
  styleUrl: './http-routing-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HttpRoutingFieldsComponent extends ZefFormBase<HttpRoutingFormState> {

  // # Deps
  #cdRef = inject(ChangeDetectorRef);

  activeLocationConfiguration: number;
  dialogOpen: boolean;

  @Input()
  mode: HttpRoutingFieldsModes = HttpRoutingFieldsModes.Normal;

  @Input()
  domainsControl: FormControlState<Boxed<string[]>>;

  @Input()
  domainsBlacklist: string[];

  @Input()
  sslControl: FormControlState<boolean>;

  @Input()
  cdnControl: FormControlState<boolean>;

  @Input()
  locationsControl: FormArrayState<HttpRoutingFormLocationsValue>;

  @Input()
  portsWithServiceStackInfo: HttpPortsWithServiceStackInfo[][];

  @Input()
  translations: HttpRoutingFieldsTranslations;

  @Output()
  removeLocation = new EventEmitter<number>();

  @Output()
  domainSelected = new EventEmitter<string>();

  @Output()
  domainBlacklisted = new EventEmitter<string>();

  @Output()
  removeBasicAuthUser = new EventEmitter<{
    locationIndex: number;
    index: number;
  }>();

  @Output()
  addBasicAuthUser = new EventEmitter<number>();

  @Output()
  resetAdvancedConfig = new EventEmitter<{
    locationIndex: number;
    data: HttpRoutingFormLocationsConfigValue;
  }>();

  httpRoutingFieldsModes = HttpRoutingFieldsModes;
  #httpRoutingFormLocationsValue: HttpRoutingFormLocationsConfigValue;

  // CIDR value converter for textarea array handling
  cidrValueConverter: NgrxValueConverter<string, string[]> = {
    convertViewToStateValue: (value: string) => {
      return value
        ? value.split('\n').map(line => line.trim()).filter(line => line.length > 0)
        : [];
    },
    convertStateToViewValue: (value: string[]) => {
      return Array.isArray(value) ? value.join('\n') : '';
    }
  };

  compareFn(c1: { port: number; serviceStackId: string; }, c2: { port: number; serviceStackId: string; }): boolean {
    return c1 && c2 && c1.port === c2.port && c1.serviceStackId === c2.serviceStackId;
  }

  getServiceStackName(serviceStackId: string, port: number): string {
    if (!this.portsWithServiceStackInfo || !serviceStackId) {
      return 'service';
    }

    for (const serviceGroup of this.portsWithServiceStackInfo) {
      const portItem = serviceGroup.find(item =>
        item.serviceStackId === serviceStackId && item.port === port
      );
      if (portItem) {
        return portItem.serviceStackName;
      }
    }

    return 'service';
  }

  openAdvancedConfig(index: number) {
    this.activeLocationConfiguration = index;
    this.dialogOpen = true;

    this.#httpRoutingFormLocationsValue = this.locationsControl.controls[index].value?.config;
  }

  openLocationAdvancedConfigByPath(path: string) {
    const pathIndex = this.locationsControl?.controls.findIndex((itm) => itm.value.path === path);

    if (pathIndex > -1) {
      this.openAdvancedConfig(pathIndex);
      this.#cdRef.detectChanges();
    }
  }

  applyCloseAdvancedConfig() {
    this.dialogOpen = false;

    this.#onAdvancedDialogClose();
  }

  closeAdvancedConfig() {
    this.resetAdvancedConfig.emit({
      locationIndex: this.activeLocationConfiguration,
      data: this.#httpRoutingFormLocationsValue
    });

    this.dialogOpen = false;

    this.#onAdvancedDialogClose();
  }

  #onAdvancedDialogClose() {
    this.activeLocationConfiguration = undefined;
    this.#httpRoutingFormLocationsValue = undefined;
  }
}

